import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { coreApi } from '@/integrations/api/active';
import { orgChartTreeRows } from '@/modules/org-chart/org-chart-tree';
import type { MergeBase } from '@/modules/org-chart/org-chart-diff';
import type {
  LinkLineagesResult,
  MergeApplyResult,
  MergeDirection,
  MergeRequest,
  OrgChart,
  OrgChartNodeKind,
  OrgChartSeat,
  OrgChartSubtreeGrant,
  Responsibility,
  SaveResult,
} from '@/modules/org-chart/types';

/**
 * Unlike every other store here, org chart edits are held locally and persisted
 * by an explicit save. They have to be: the single-root and no-cycle invariants
 * are deferred to commit, so a per-row write path could not satisfy them, and a
 * restructuring that spans many seats must not be able to half-land.
 *
 * `seats` is the working copy the UI edits. `baseline` is the last saved state,
 * kept for the dirty check and for the audit diff.
 */
interface OrgChartState {
  charts: OrgChart[];
  activeChartId: string | null;
  seats: OrgChartSeat[];
  baseline: OrgChartSeat[];
  /**
   * The trunk's seats, held separately from the working copy. Everything
   * outside the org chart view — People Insights, Planning, the sidebar —
   * answers questions about the organisation as it stands, which is the trunk,
   * not whichever fork happens to be selected.
   */
  trunkSeats: OrgChartSeat[];
  loading: boolean;
  saving: boolean;
  workspaceId: string | null;
  /** Subtree grants on the active chart, RLS-filtered — only what this actor may see (#219). */
  grants: OrgChartSubtreeGrant[];

  /**
   * The node the rest of the interface is talking about, addressed by lineage
   * because that is what the diff addresses and what survives a fork. Phase B's
   * diff is its first consumer; E's team controls and F's grant controls
   * attach to the same contract rather than inventing their own.
   */
  selectedLineageId: string | null;
  /** The other chart's grants let client-side merge gates mirror authority in either direction. */
  compare: {
    other: OrgChart;
    otherSeats: OrgChartSeat[];
    otherGrants?: OrgChartSubtreeGrant[];
    base: MergeBase;
    pairId: string | null;
    pairUpdatedAt: string | null;
  } | null;
  merging: boolean;

  reset: () => void;
  fetchCharts: (workspaceId: string) => Promise<void>;
  /** For callers that only need the trunk's seats. */
  fetchSeats: (workspaceId: string) => Promise<void>;
  selectChart: (chartId: string) => Promise<void>;

  /**
   * Snapshots of the working copy, newest last. Snapshots rather than inverse
   * operations: at chart scale the memory is nothing, and restoring a subtree
   * deleted with its children is then correct by construction.
   */
  past: OrgChartSeat[][];
  future: OrgChartSeat[][];
  /**
   * While open, every mutation folds into the one snapshot taken before the
   * first of them — a session's worth of edits undoes as the single act it was.
   */
  historyBatch: { pushed: boolean } | null;
  openHistoryBatch: () => void;
  closeHistoryBatch: () => void;

  addSeat: (seat: Partial<OrgChartSeat> & { parentLineageId: string | null }) => OrgChartSeat | null;
  addSupportRole: (parentLineageId: string) => OrgChartSeat | null;
  addBoardMember: () => OrgChartSeat | null;
  convertSeatKind: (
    lineageId: string,
    nodeKind: Extract<OrgChartNodeKind, 'seat' | 'sidecar'>,
  ) => { ok: boolean; error?: string };
  updateSeat: (lineageId: string, updates: Partial<OrgChartSeat>) => void;
  deleteSeat: (lineageId: string, mode: 'delete-children' | 'reassign-children') => void;
  /**
   * One operation, always: a drop sets `(parent, index)`. Omitting the index
   * appends, which is what dropping onto a card rather than into a position
   * means.
   */
  moveSeat: (lineageId: string, newParentLineageId: string | null, index?: number) => void;
  duplicateSeat: (lineageId: string) => void;
  undo: () => void;
  redo: () => void;
  discard: () => void;
  save: () => Promise<{ ok: boolean; error?: string }>;
  forkChart: (
    sourceChartId: string,
    name: string,
    targetTeamId: string,
    targetDate: string | null,
  ) => Promise<{ ok: boolean; error?: string }>;
  /**
   * Gate for anything that takes the active chart off screen. The working copy
   * is module state, so without this it would outlive the view and reappear
   * against whichever chart is shown next.
   */
  confirmLeave: (onProceed?: () => void) => boolean;

  /** Set while the leave dialog is open; holds what to run if the user accepts. */
  leavePrompt: (() => void) | null;
  resolveLeave: (proceed: boolean) => void;

  selectLineage: (lineageId: string | null) => void;
  openCompare: (otherChartId: string) => Promise<{ ok: boolean; error?: string }>;
  closeCompare: () => void;
  applyMerge: (
    direction: MergeDirection,
    requests: MergeRequest[],
  ) => Promise<{ ok: boolean; error?: string; result?: MergeApplyResult }>;
  linkLineages: (
    displayedLineageId: string,
    otherLineageId: string,
  ) => Promise<{ ok: boolean; error?: string; result?: LinkLineagesResult }>;

  /** Chart-manager only; RLS is the real gate. Grant roots are always ordinary seats. */
  createGrant: (rootLineageId: string, teamId: string) => Promise<{ ok: boolean; error?: string }>;
  revokeGrant: (grantId: string) => Promise<{ ok: boolean; error?: string }>;

  /**
   * Phase H (#221): non-trunk fork lifecycle. Operate on any chart, not just
   * the active one — the config modal lists every chart the actor can manage.
   * Never optimistic: each sends the chart's exact `updatedAt` and waits for
   * the RPC before touching local state.
   */
  archiveChart: (chartId: string) => Promise<{ ok: boolean; error?: string }>;
  restoreChart: (chartId: string) => Promise<{ ok: boolean; error?: string }>;
  deleteChart: (chartId: string) => Promise<{ ok: boolean; error?: string }>;
  /**
   * A direct write, not an RPC: Phase H grants `authenticated` UPDATE(name,
   * target_date) on `org_charts`, and `guard_org_chart_immutables` already
   * refuses it once the chart is archived or deleted.
   */
  renameChart: (chartId: string, name: string) => Promise<{ ok: boolean; error?: string }>;

  isDirty: () => boolean;
  activeChart: () => OrgChart | null;
  trunk: () => OrgChart | null;
}

function chartFromRow(row: any): OrgChart {
  return {
    id: row.id,
    parentChartId: row.parent_chart_id,
    teamId: row.team_id,
    name: row.name,
    status: row.status,
    targetDate: row.target_date ?? null,
    updatedAt: row.updated_at,
  };
}

function seatFromRow(row: any): OrgChartSeat {
  return {
    id: row.id,
    chartId: row.chart_id,
    lineageId: row.lineage_id,
    parentLineageId: row.parent_lineage_id,
    nodeKind: row.node_kind ?? 'seat',
    title: row.title ?? '',
    ownerId: row.owner_id,
    responsibilities: (row.responsibilities ?? []) as Responsibility[],
    sortOrder: row.sort_order ?? 0,
    tags: row.tags ?? [],
    idealDiscScores: row.ideal_disc_scores ?? null,
  };
}

function grantFromRow(row: any): OrgChartSubtreeGrant {
  return {
    id: row.id,
    chartId: row.chart_id,
    rootLineageId: row.root_lineage_id,
    teamId: row.team_id,
    createdBy: row.created_by,
    createdAt: row.created_at,
  };
}

function seatToPayload(seat: OrgChartSeat) {
  return {
    lineage_id: seat.lineageId,
    parent_lineage_id: seat.parentLineageId,
    node_kind: seat.nodeKind,
    title: seat.title,
    owner_id: seat.ownerId,
    tags: seat.tags,
    responsibilities: seat.responsibilities,
    sort_order: seat.sortOrder,
    ideal_disc_scores: seat.idealDiscScores ?? null,
  };
}

/**
 * `sort_order` is gapped rather than dense. It is a merge field, so renumbering
 * a whole sibling list on every drag would enter each of those siblings into
 * the next diff as a difference nobody made. A midpoint insert touches one row.
 */
const SORT_STEP = 1024;

/** Siblings under `parentLineageId`, in order, excluding `exclude`. */
function siblingsOf(
  seats: OrgChartSeat[],
  parentLineageId: string | null,
  exclude?: string,
  nodeKind?: OrgChartNodeKind,
): OrgChartSeat[] {
  return seats
    .filter(
      (s) =>
        s.parentLineageId === parentLineageId &&
        s.lineageId !== exclude &&
        (nodeKind === undefined || s.nodeKind === nodeKind),
    )
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * The sort order for a seat landing at `index` among `siblings`, plus any
 * renumbering the insert forces. Renumbering only happens when the midpoint
 * would collide with a neighbour, which gapping makes rare.
 */
function orderAt(
  siblings: OrgChartSeat[],
  index: number,
): { sortOrder: number; renumbered: Map<string, number> | null } {
  const before = index > 0 ? siblings[index - 1]?.sortOrder : undefined;
  const after = index < siblings.length ? siblings[index]?.sortOrder : undefined;

  if (before === undefined && after === undefined) return { sortOrder: 0, renumbered: null };
  if (before === undefined) return { sortOrder: after! - SORT_STEP, renumbered: null };
  if (after === undefined) return { sortOrder: before + SORT_STEP, renumbered: null };
  if (after - before > 1) return { sortOrder: Math.floor((before + after) / 2), renumbered: null };

  const renumbered = new Map(siblings.map((s, i) => [s.lineageId, i * SORT_STEP * 2]));
  return { sortOrder: index * SORT_STEP * 2 - SORT_STEP, renumbered };
}

/** Deep enough for a working session; the stack is dropped on save regardless. */
const HISTORY_LIMIT = 60;

type SetState = (partial: Partial<OrgChartState> | ((s: OrgChartState) => Partial<OrgChartState>)) => void;

/**
 * Called by every seat mutation before it writes. Field edits reach this once
 * per committed value rather than once per keystroke, because their call sites
 * commit on blur or on an explicit choice.
 */
function pushHistory(set: SetState, get: () => OrgChartState) {
  const batch = get().historyBatch;
  if (batch?.pushed) return;
  if (batch) set({ historyBatch: { pushed: true } });
  set({ past: [...get().past, get().seats].slice(-HISTORY_LIMIT), future: [] });
}

async function loadSeats(chartId: string): Promise<OrgChartSeat[]> {
  const { data } = await supabase.from('org_chart_seats').select('*').eq('chart_id', chartId).order('sort_order');
  return (data ?? []).map(seatFromRow);
}

async function loadGrants(chartId: string): Promise<OrgChartSubtreeGrant[]> {
  const { data } = await supabase.from('org_chart_subtree_grants').select('*').eq('chart_id', chartId);
  return (data ?? []).map(grantFromRow);
}

async function loadCharts(workspaceId: string): Promise<OrgChart[]> {
  const { data } = await supabase.from('org_charts').select('*').eq('workspace_id', workspaceId).order('created_at');
  return (data ?? []).map(chartFromRow);
}

function lifecycleErrorMessage(error: { code?: string; message: string; details?: string }): string {
  return error.code === '40001'
    ? 'This chart changed since it was loaded. Refresh before retrying.'
    : error.details || error.message;
}

let _orgChartRequestGeneration = 0;

/**
 * Org-chart-owned server cache and transient working copy, keyed by workspace.
 * History and dirty state reset atomically with the loaded chart state.
 */
export const useOrgChartStore = create<OrgChartState>((set, get) => ({
  charts: [],
  activeChartId: null,
  seats: [],
  baseline: [],
  trunkSeats: [],
  loading: false,
  saving: false,
  leavePrompt: null,
  workspaceId: null,
  grants: [],
  selectedLineageId: null,
  compare: null,
  merging: false,
  past: [],
  future: [],
  historyBatch: null,

  // Cleared on an account change (#142) — see AuthContext. A Zustand store is a
  // module singleton, so seats fetched under one user's RLS survive a sign-out
  // and render to the next one until their own fetch resolves.
  reset: () => {
    _orgChartRequestGeneration += 1;
    set({
      charts: [],
      activeChartId: null,
      seats: [],
      baseline: [],
      trunkSeats: [],
      historyBatch: null,
      past: [],
      future: [],
      loading: false,
      saving: false,
      workspaceId: null,
      grants: [],
      selectedLineageId: null,
      compare: null,
      merging: false,
    });
  },

  fetchCharts: async (workspaceId: string) => {
    const requestGeneration = ++_orgChartRequestGeneration;
    // A selection belongs to the workspace it was made in; carrying it across
    // would leave a stale fork id selected against the new workspace's charts.
    const switching = get().workspaceId !== workspaceId;
    set({
      loading: true,
      workspaceId,
      ...(switching ? { activeChartId: null, seats: [], baseline: [], trunkSeats: [] } : {}),
    });

    const { data } = await supabase
      .from('org_charts')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('created_at');
    if (
      requestGeneration !== _orgChartRequestGeneration
      || get().workspaceId !== workspaceId
    ) return;
    const charts = (data ?? []).map(chartFromRow);
    set({ charts });

    const trunkId = charts.find((c) => c.parentChartId === null)?.id ?? null;
    const active = get().activeChartId ?? trunkId;
    if (trunkId && trunkId !== active) {
      const trunkSeats = await loadSeats(trunkId);
      if (
        requestGeneration !== _orgChartRequestGeneration
        || get().workspaceId !== workspaceId
      ) return;
      set({ trunkSeats });
    }

    if (active) {
      await get().selectChart(active);
    } else {
      set({ loading: false });
    }
  },

  fetchSeats: async (workspaceId: string) => {
    if (get().workspaceId === workspaceId && get().trunkSeats.length) return;
    await get().fetchCharts(workspaceId);
  },

  selectChart: async (chartId: string) => {
    const requestGeneration = ++_orgChartRequestGeneration;
    const workspaceId = get().workspaceId;
    set({
      loading: true,
      activeChartId: chartId,
      selectedLineageId: null,
      compare: null,
      past: [],
      future: [],
      historyBatch: null,
    });
    const [seats, grants] = await Promise.all([loadSeats(chartId), loadGrants(chartId)]);
    if (
      requestGeneration !== _orgChartRequestGeneration
      || get().workspaceId !== workspaceId
      || get().activeChartId !== chartId
    ) return;
    const isTrunkChart = get().charts.find((c) => c.id === chartId)?.parentChartId === null;
    set({ seats, baseline: seats, grants, loading: false, ...(isTrunkChart ? { trunkSeats: seats } : {}) });
  },

  addSeat: (seat) => {
    const { activeChartId, seats } = get();
    if (!activeChartId) return null;
    const nodeKind = seat.nodeKind ?? 'seat';
    const siblings = siblingsOf(seats, seat.parentLineageId, undefined, nodeKind);
    const next: OrgChartSeat = {
      id: crypto.randomUUID(),
      chartId: activeChartId,
      lineageId: crypto.randomUUID(),
      parentLineageId: seat.parentLineageId,
      nodeKind,
      title: seat.title ?? '',
      ownerId: seat.ownerId ?? null,
      responsibilities: seat.responsibilities ?? [],
      sortOrder: seat.sortOrder ?? (siblings.length ? siblings[siblings.length - 1].sortOrder + SORT_STEP : 0),
      tags: seat.tags ?? [],
      idealDiscScores: seat.idealDiscScores ?? null,
    };
    pushHistory(set, get);
    set({ seats: [...seats, next] });
    return next;
  },

  addSupportRole: (parentLineageId) => {
    const parent = get().seats.find((s) => s.lineageId === parentLineageId);
    if (!parent || parent.nodeKind !== 'seat') return null;
    return get().addSeat({ parentLineageId, nodeKind: 'sidecar', title: 'Support role' });
  },

  addBoardMember: () => get().addSeat({ parentLineageId: null, nodeKind: 'board', title: 'Board member' }),

  convertSeatKind: (lineageId, nodeKind) => {
    const { seats } = get();
    const seat = seats.find((s) => s.lineageId === lineageId);
    if (!seat) return { ok: false, error: 'That role is no longer in this chart.' };
    if (seat.nodeKind === 'board') return { ok: false, error: 'Board members cannot be converted.' };
    if (seat.nodeKind === nodeKind) return { ok: true };

    const parent = seats.find((s) => s.lineageId === seat.parentLineageId);
    if (!parent || parent.nodeKind !== 'seat') {
      return { ok: false, error: 'The top seat cannot become a support role.' };
    }
    if (nodeKind === 'sidecar' && seats.some((s) => s.parentLineageId === lineageId)) {
      return { ok: false, error: 'Move or remove every attached role before converting this seat.' };
    }

    pushHistory(set, get);
    set({ seats: seats.map((s) => (s.lineageId === lineageId ? { ...s, nodeKind } : s)) });
    return { ok: true };
  },

  updateSeat: (lineageId, updates) => {
    pushHistory(set, get);
    set((s) => ({
      seats: s.seats.map((seat) => (seat.lineageId === lineageId ? { ...seat, ...updates } : seat)),
    }));
  },

  deleteSeat: (lineageId, mode) => {
    const { seats } = get();
    const seat = seats.find((s) => s.lineageId === lineageId);
    if (!seat) return;
    pushHistory(set, get);

    if (mode === 'reassign-children') {
      set({
        seats: seats
          .filter((s) => s.lineageId !== lineageId)
          .map((s) => (s.parentLineageId === lineageId ? { ...s, parentLineageId: seat.parentLineageId } : s)),
      });
      return;
    }

    const doomed = new Set<string>();
    const collect = (id: string) => {
      doomed.add(id);
      seats.filter((s) => s.parentLineageId === id).forEach((s) => collect(s.lineageId));
    };
    collect(lineageId);
    set({ seats: seats.filter((s) => !doomed.has(s.lineageId)) });
  },

  moveSeat: (lineageId, newParentLineageId, index) => {
    const { seats } = get();
    const moving = seats.find((s) => s.lineageId === lineageId);
    if (!moving) return;

    if (moving.nodeKind === 'board' && newParentLineageId !== null) return;
    if (moving.nodeKind !== 'board') {
      const parent = seats.find((s) => s.lineageId === newParentLineageId);
      if (!parent || parent.nodeKind !== 'seat') return;
    }

    const siblings = siblingsOf(seats, newParentLineageId, lineageId, moving.nodeKind);
    const at = index === undefined ? siblings.length : Math.max(0, Math.min(index, siblings.length));
    const { sortOrder, renumbered } = orderAt(siblings, at);
    if (moving.parentLineageId === newParentLineageId && moving.sortOrder === sortOrder && !renumbered) return;

    pushHistory(set, get);
    set({
      seats: seats.map((s) => {
        if (s.lineageId === lineageId) return { ...s, parentLineageId: newParentLineageId, sortOrder };
        const renumber = renumbered?.get(s.lineageId);
        return renumber === undefined ? s : { ...s, sortOrder: renumber };
      }),
    });
  },

  duplicateSeat: (lineageId) => {
    const seat = get().seats.find((s) => s.lineageId === lineageId);
    if (!seat) return;
    const siblings = siblingsOf(get().seats, seat.parentLineageId, undefined, seat.nodeKind);
    const at = siblings.findIndex((s) => s.lineageId === lineageId) + 1;
    const { sortOrder, renumbered } = orderAt(siblings, at);
    // A duplicate is a different seat, so it gets its own lineage rather than
    // colliding with the original in every fork.
    get().addSeat({ ...seat, parentLineageId: seat.parentLineageId, ownerId: null, sortOrder });
    if (renumbered) {
      set((s) => ({
        seats: s.seats.map((x) => {
          const next = renumbered.get(x.lineageId);
          return next === undefined ? x : { ...x, sortOrder: next };
        }),
      }));
    }
  },

  undo: () => {
    const { past, seats } = get();
    if (past.length === 0) return;
    set({ seats: past[past.length - 1], past: past.slice(0, -1), future: [...get().future, seats] });
  },

  redo: () => {
    const { future, seats } = get();
    if (future.length === 0) return;
    set({ seats: future[future.length - 1], future: future.slice(0, -1), past: [...get().past, seats] });
  },

  openHistoryBatch: () => set({ historyBatch: { pushed: false } }),
  closeHistoryBatch: () => set({ historyBatch: null }),

  discard: () =>
    set((s) => ({ seats: s.baseline, selectedLineageId: null, past: [], future: [], historyBatch: null })),

  save: async () => {
    const { activeChartId, seats, charts } = get();
    const chart = charts.find((c) => c.id === activeChartId);
    if (!activeChartId || !chart) return { ok: false, error: 'No chart selected' };

    set({ saving: true });
    const { data: saved, error } = await coreApi(`/org-charts/${activeChartId}/save`, {
      payload: { seats: seats.map(seatToPayload) },
      expected_updated_at: chart.updatedAt,
    });

    if (error) {
      set({ saving: false });
      return {
        ok: false,
        error:
          error.code === '40001'
            ? 'This chart changed while you were editing it. Reload to see the current version.'
            : error.details || error.message,
      };
    }

    // Undo is over unsaved work; once it is saved there is nothing to take back
    // without a new edit.
    set({ past: [], future: [], historyBatch: null });

    const result = saved?.[0] as { updated_at: string };

    set((s) => ({
      charts: s.charts.map((c) => (c.id === activeChartId ? { ...c, updatedAt: result.updated_at } : c)),
    }));
    // Re-read so server-assigned row ids land on newly created seats. That
    // clears any open comparison, so re-open it against the saved chart.
    const openAgainst = get().compare?.other.id ?? null;
    await get().selectChart(activeChartId);
    if (openAgainst) await get().openCompare(openAgainst);
    set({ saving: false });
    return { ok: true };
  },

  forkChart: async (sourceChartId, name, targetTeamId, targetDate) => {
    const { workspaceId } = get();
    const { data: forked, error } = await coreApi(`/org-charts/${sourceChartId}/fork`, {
      name,
      target_team_id: targetTeamId ?? null,
      target_date: targetDate ?? null,
    });
    if (error) return { ok: false, error: error.details || error.message };
    const data = (forked?.[0] as { id?: string | null } | undefined)?.id;

    if (workspaceId) await get().fetchCharts(workspaceId);
    if (typeof data === 'string') await get().selectChart(data);
    return { ok: true };
  },

  confirmLeave: (onProceed) => {
    if (!get().isDirty()) return true;
    set({ leavePrompt: onProceed ?? (() => {}) });
    return false;
  },

  resolveLeave: (proceed) => {
    const pending = get().leavePrompt;
    set({ leavePrompt: null });
    if (!proceed) return;
    get().discard();
    pending?.();
  },

  selectLineage: (lineageId) => set({ selectedLineageId: lineageId }),

  openCompare: async (otherChartId: string) => {
    const { charts, activeChartId } = get();
    const active = charts.find((c) => c.id === activeChartId);
    const other = charts.find((c) => c.id === otherChartId);
    if (!active || !other) return { ok: false, error: 'That chart is not available' };
    const { data, error } = await supabase.rpc('load_org_chart_comparison', {
      _displayed_chart_id: active.id,
      _other_chart_id: other.id,
    } as any);
    if (error) return { ok: false, error: error.message };

    const [otherSeats, otherGrants] = await Promise.all([loadSeats(other.id), loadGrants(other.id)]);
    const comparison = data as any;
    const currentOther = comparison?.other_updated_at ?? other.updatedAt;
    set((state) => ({
      charts: state.charts.map((chart) =>
        chart.id === active.id
          ? { ...chart, updatedAt: comparison?.displayed_updated_at ?? chart.updatedAt }
          : chart.id === other.id
            ? { ...chart, updatedAt: currentOther }
            : chart,
      ),
      compare: {
        other: { ...other, updatedAt: currentOther },
        otherSeats,
        otherGrants,
        base: (comparison?.base ?? {}) as MergeBase,
        pairId: comparison?.pair_id ?? null,
        pairUpdatedAt: comparison?.pair_updated_at ?? null,
      },
    }));
    return { ok: true };
  },

  closeCompare: () => set({ compare: null }),

  applyMerge: async (direction, requests) => {
    const { activeChartId, charts, compare } = get();
    const fork = charts.find((c) => c.id === activeChartId);
    if (!fork || !compare) return { ok: false, error: 'Nothing to merge' };
    if (!requests.length) return { ok: false, error: 'Nothing selected' };
    // A merge reads and re-reads the database, so unsaved edits would neither
    // travel with it nor survive the re-read.
    if (get().isDirty()) return { ok: false, error: 'Save your changes before syncing.' };

    const push = direction === 'push';
    const target = push ? compare.other : fork;
    const source = push ? fork : compare.other;

    set({ merging: true });
    const { data: merged, error } = await coreApi('/org-charts/merge', {
      source_chart_id: push ? fork.id : compare.other.id,
      target_chart_id: target.id,
      payload: {
        seats: requests.map((r) => {
          if (!r.fields?.includes('node_kind')) {
            return {
              lineage_id: r.lineageId,
              ...(r.fields ? { fields: r.fields } : {}),
              ...(r.action ? { action: r.action } : {}),
              ...(r.descendants !== undefined ? { descendants: r.descendants } : {}),
            };
          }
          const fields = new Set(r.fields);
          fields.add('parent_lineage_id');
          return {
            lineage_id: r.lineageId,
            fields: [...fields],
            ...(r.action ? { action: r.action } : {}),
            ...(r.descendants !== undefined ? { descendants: r.descendants } : {}),
          };
        }),
      },
      expected_source_updated_at: source.updatedAt,
      expected_target_updated_at: target.updatedAt,
      expected_pair_updated_at: compare.pairUpdatedAt,
    });
    set({ merging: false });

    if (error) {
      return {
        ok: false,
        error:
          error.code === '40001'
            ? 'That chart changed while you were reviewing. Reopen the comparison to see the current version.'
            : error.details || error.message,
      };
    }

    const raw = merged?.[0] as any;
    const result: MergeApplyResult = {
      targetUpdatedAt: raw.target_updated_at,
      sourceUpdatedAt: raw.source_updated_at,
      applied: (raw.applied ?? []).map((a: any) => ({
        id: a.id,
        lineageId: a.lineage_id,
        title: a.title,
        changed: a.changed ?? [],
        added: !!a.added,
        deleted: !!a.deleted,
      })),
      skipped: (raw.skipped ?? []).map((s: any) => ({ lineageId: s.lineage_id, reason: s.reason })),
      pairUpdatedAt: raw.pair_updated_at ?? null,
    };

    // Pair-only agreement updates leave chart stamps unchanged; adopt the RPC's
    // stamps so the next save starts from the server's actual comparison state.
    set((s) => ({
      charts: s.charts.map((c) =>
        c.id === target.id
          ? { ...c, updatedAt: result.targetUpdatedAt }
          : c.id === source.id
            ? { ...c, updatedAt: result.sourceUpdatedAt }
            : c,
      ),
    }));

    const otherId = compare.other.id;
    await get().selectChart(fork.id);
    await get().openCompare(otherId);
    return { ok: true, result };
  },

  linkLineages: async (displayedLineageId, otherLineageId) => {
    const { activeChartId, charts, compare } = get();
    const displayed = charts.find((chart) => chart.id === activeChartId);
    if (!displayed || !compare) return { ok: false, error: 'Nothing to link' };
    if (get().isDirty()) return { ok: false, error: 'Save your changes before syncing.' };

    set({ merging: true });
    const { data: linked, error } = await coreApi('/org-charts/link-lineages', {
      displayed_chart_id: displayed.id,
      other_chart_id: compare.other.id,
      displayed_lineage_id: displayedLineageId,
      other_lineage_id: otherLineageId,
      expected_displayed_updated_at: displayed.updatedAt,
      expected_other_updated_at: compare.other.updatedAt,
      expected_pair_updated_at: compare.pairUpdatedAt,
    });
    set({ merging: false });

    if (error) {
      return {
        ok: false,
        error: error.code === '40001'
          ? 'That chart changed while you were reviewing. Reopen the comparison to see the current version.'
          : error.details || error.message,
      };
    }

    const raw = linked?.[0] as any;
    const result: LinkLineagesResult = {
      displayedRowId: raw.displayed_row_id,
      displayedTitle: raw.displayed_title,
      linkedRowId: raw.linked_row_id,
      linkedTitle: raw.linked_title,
      oldLineageId: raw.old_lineage_id,
      newLineageId: raw.new_lineage_id,
      affectedCharts: raw.affected_charts,
      displayedUpdatedAt: raw.displayed_updated_at,
      otherUpdatedAt: raw.other_updated_at,
    };

    set((state) => ({
      charts: state.charts.map((chart) =>
        chart.id === displayed.id
          ? { ...chart, updatedAt: result.displayedUpdatedAt }
          : chart.id === compare.other.id
            ? { ...chart, updatedAt: result.otherUpdatedAt }
            : chart,
      ),
    }));

    const otherId = compare.other.id;
    await get().selectChart(displayed.id);
    await get().openCompare(otherId);
    return { ok: true, result };
  },

  createGrant: async (rootLineageId, teamId) => {
    const { activeChartId, workspaceId } = get();
    if (!activeChartId || !workspaceId) return { ok: false, error: 'No chart selected' };
    const { data, error } = await coreApi('/org-chart-grants', {
      chart_id: activeChartId,
      root_lineage_id: rootLineageId,
      team_id: teamId,
    });
    if (error || !data?.[0]) return { ok: false, error: error?.details || error?.message || 'That could not be granted.' };

    const grant = grantFromRow(data[0]);
    set((s) => ({ grants: [...s.grants, grant] }));
    return { ok: true };
  },

  revokeGrant: async (grantId) => {
    const { error } = await coreApi(`/org-chart-grants/${grantId}`, {}, 'DELETE');
    if (error) return { ok: false, error: error.details || error.message };

    set((s) => ({ grants: s.grants.filter((g) => g.id !== grantId) }));
    return { ok: true };
  },

  archiveChart: async (chartId) => {
    const { charts, workspaceId } = get();
    const chart = charts.find((c) => c.id === chartId);
    if (!chart) return { ok: false, error: 'That chart is not available' };

    const { error } = await coreApi(`/org-charts/${chartId}/archive`, { expected_updated_at: chart.updatedAt });
    if (error) return { ok: false, error: lifecycleErrorMessage(error) };

    if (workspaceId) set({ charts: await loadCharts(workspaceId) });
    return { ok: true };
  },

  restoreChart: async (chartId) => {
    const { charts, workspaceId } = get();
    const chart = charts.find((c) => c.id === chartId);
    if (!chart) return { ok: false, error: 'That chart is not available' };

    const { error } = await coreApi(`/org-charts/${chartId}/restore`, { expected_updated_at: chart.updatedAt });
    if (error) return { ok: false, error: lifecycleErrorMessage(error) };

    if (workspaceId) set({ charts: await loadCharts(workspaceId) });
    return { ok: true };
  },

  deleteChart: async (chartId) => {
    const { charts, workspaceId, activeChartId, compare } = get();
    const chart = charts.find((c) => c.id === chartId);
    if (!chart) return { ok: false, error: 'That chart is not available' };

    const { error } = await coreApi(`/org-charts/${chartId}`, { expected_updated_at: chart.updatedAt }, 'DELETE');
    if (error) return { ok: false, error: lifecycleErrorMessage(error) };

    const nextCharts = workspaceId ? await loadCharts(workspaceId) : [];
    const closingCompare = compare?.other.id === chartId;

    if (activeChartId !== chartId) {
      set({ charts: nextCharts, ...(closingCompare ? { compare: null } : {}) });
      return { ok: true };
    }

    // The active chart is gone: fall back to the trunk, then the first
    // readable chart in canonical switcher order, then the empty state.
    set({ charts: nextCharts, compare: null });
    const trunkId = nextCharts.find((c) => c.parentChartId === null)?.id ?? null;
    const fallbackId = trunkId ?? orgChartTreeRows(nextCharts)[0]?.chart.id ?? null;
    if (fallbackId) {
      await get().selectChart(fallbackId);
    } else {
      set({
        activeChartId: null,
        seats: [],
        baseline: [],
        grants: [],
        selectedLineageId: null,
        past: [],
        future: [],
        historyBatch: null,
      });
    }
    return { ok: true };
  },

  renameChart: async (chartId, name) => {
    const { charts, workspaceId } = get();
    const chart = charts.find((c) => c.id === chartId);
    if (!chart) return { ok: false, error: 'That chart is not available' };
    const trimmed = name.trim();
    if (!trimmed || trimmed === chart.name) return { ok: true };

    const { error } = await coreApi(`/records/org-charts/${chartId}`, { name: trimmed }, 'PATCH');
    if (error) return { ok: false, error: error.details || error.message };

    if (workspaceId) set({ charts: await loadCharts(workspaceId) });
    return { ok: true };
  },

  isDirty: () => {
    const { seats, baseline } = get();
    if (seats.length !== baseline.length) return true;
    const before = new Map(baseline.map((s) => [s.lineageId, JSON.stringify(seatToPayload(s))]));
    // Compared on the save payload, so a field the RPC ignores cannot make a
    // chart look unsaved.
    return seats.some((s) => before.get(s.lineageId) !== JSON.stringify(seatToPayload(s)));
  },

  activeChart: () => get().charts.find((c) => c.id === get().activeChartId) ?? null,
  trunk: () => get().charts.find((c) => c.parentChartId === null) ?? null,
}));
